import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select } from "@/components/ui/select";
import { ApiError, api, apiErrorMessage } from "@/lib/api";
import { Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * `/admin/seafile-folders`: pair each project folder in Seafile with its Hub
 * project in one go. The pairing by name is only a suggestion to confirm.
 */

export const Route = createFileRoute("/_app/admin/seafile-folders")({
  component: SeafileFoldersPage,
});

type Folder = {
  library: string;
  name: string;
  boundProjectId: string | null;
  suggestedProjectId: string | null;
};
type Project = { id: string; name: string; seafileFolder: string | null; archived: boolean };

const key = (f: Folder) => `${f.library}/${f.name}`;

function SeafileFoldersPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [folders, setFolders] = useState<Folder[] | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [picked, setPicked] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ folders: Folder[]; projects: Project[] }>(
        "/api/admin/seafile-folders",
      );
      setFolders(res.folders);
      setProjects(res.projects);
      setPicked(Object.fromEntries(res.folders.map((f) => [key(f), f.suggestedProjectId ?? ""])));
    } catch (err) {
      setMessage(
        err instanceof ApiError && err.status === 503
          ? "Seafile isn't configured (SEAFILE_URL / SEAFILE_TOKEN)."
          : `Couldn't read the folders from Seafile: ${apiErrorMessage(err)}`,
      );
    }
  }, []);

  useEffect(() => {
    if (user.globalRole === "admin") void load();
  }, [user.globalRole, load]);

  if (user.globalRole !== "admin") return <Navigate to="/" />;

  const chosen = Object.values(picked).filter(Boolean);
  const duplicates = new Set(chosen.filter((id, i) => chosen.indexOf(id) !== i));

  async function bind() {
    if (!folders) return;
    setBusy(true);
    setMessage(null);
    try {
      const rows = folders
        .filter((f) => picked[key(f)])
        .map((f) => ({ projectId: picked[key(f)], folder: f.name, library: f.library }));
      const res = await api.post<{ bound: number }>("/api/admin/seafile-folders/bind", { rows });
      setMessage(`Bound ${res.bound} folder(s).`);
      await load();
    } catch (err) {
      setMessage(`Couldn't bind them: ${apiErrorMessage(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">Seafile folders</h1>
          <p className="text-sm text-muted-foreground">
            Every folder in the Projekty and Archiv libraries, with the project it looks like it
            belongs to. Check the pairs, fix what's wrong, and bind them. A folder in Archiv marks
            its project archived. Nothing changes in Seafile yet: the access job still runs in dry
            mode.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => void bind()}
          disabled={busy || chosen.length === 0 || duplicates.size > 0}
          data-testid="bind-folders"
        >
          Bind {chosen.length}
        </Button>
      </div>
      {message && <p className="text-sm">{message}</p>}
      {duplicates.size > 0 && (
        <p className="text-sm text-destructive">A project is picked for more than one folder.</p>
      )}
      {folders && (
        <Card>
          <CardContent className="divide-y pt-2">
            {folders.map((f) => (
              <div
                key={key(f)}
                className="flex items-center justify-between gap-3 py-2"
                data-testid="seafile-folder"
              >
                <div className="flex items-center gap-2 text-sm">
                  <Badge variant="secondary">{f.library}</Badge>
                  <span className="font-medium">{f.name}</span>
                  {f.boundProjectId && <Badge variant="outline">bound</Badge>}
                </div>
                <Select
                  size="sm"
                  aria-label={`Project for ${f.name}`}
                  value={picked[key(f)] ?? ""}
                  onChange={(v) => setPicked((prev) => ({ ...prev, [key(f)]: v }))}
                  options={[
                    { value: "", label: "— not a project —" },
                    ...projects.map((p) => ({ value: p.id, label: p.name })),
                  ]}
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
